import { Link, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GradeKpis, Kpi } from "@/features/overview/telas/Kpi";
import { CORES } from "@/features/overview/series";
import { useAcessoInstituicao } from "./api";
import { PrimeiroAcesso } from "./AbaInstituicoes";
import { InfoAcesso, PeriodoSelect, StatusBadge } from "./componentes";
import { usePeriodoUrl } from "./usePeriodoUrl";
import { Engajamento } from "./Engajamento";
import { TabelaUsuarios } from "./TabelaUsuarios";
import { SITUACAO_ADMIN_LABEL, descreverPeriodo, formatAcesso, formatDiasSemAcesso, formatNumero, formatPercentual, tendencia } from "./formato";

/** /Acessos/Instituicao/:idInstituicao — o detalhamento por usuário de uma instituição. */
export default function DetalheAcessosInstituicao() {
  const { idInstituicao } = useParams<{ idInstituicao: string }>();
  const id = Number(idInstituicao);
  const { periodo, dias, definir } = usePeriodoUrl();
  const { data: i, isLoading } = useAcessoInstituicao(id, periodo);
  const t = i ? tendencia(i.logins, i.loginsPeriodoAnterior) : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 shadow-card">
        <div className="space-y-1">
          {isLoading ? (
            <Skeleton className="h-6 w-64" />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-foreground">{i?.nomeFantasia ?? i?.razaoSocial ?? `Instituição #${id}`}</h1>
              {i && <StatusBadge status={i.status} tipo="instituicao" />}
              {i?.reativada && <span className="text-xs text-emerald-700">reativada no período</span>}
              <InfoAcesso />
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            {[i?.codigo, SITUACAO_ADMIN_LABEL[i?.situacaoAdmin ?? ""]].filter(Boolean).join(" · ")}
          </p>
          <p className="text-sm text-foreground">
            Último acesso: <strong>{formatAcesso(i?.ultimoAcesso)}</strong>
            {i?.ultimoAcessoUsuario && ` por ${i.ultimoAcessoUsuario}`}
            {i?.diasSemAcesso != null && i.diasSemAcesso > 1 && ` (${formatDiasSemAcesso(i.diasSemAcesso)})`}
          </p>
          <p className="text-sm text-muted-foreground">
            Primeiro acesso: <PrimeiroAcesso valor={i?.primeiroAcesso} antesDoLog={i?.primeiroAcessoAntesDoLog} />
            {i?.primeiroAcessoUsuario && ` por ${i.primeiroAcessoUsuario}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodoSelect key={`${periodo.inicio}-${periodo.fim}`} periodo={periodo} dias={dias} onChange={definir} />
          <Button variant="outline" asChild>
            <Link to={`/Instituicao/Ficha/${id}`}>
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden /> Ficha da instituição
            </Link>
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">Contagens dos {descreverPeriodo(periodo, dias)}.</p>

      <GradeKpis variante="padrao" colunas={4}>
        <Kpi
          variante="padrao"
          rotulo="Logins no período"
          valor={formatNumero(i?.logins)}
          detalhe={t ? `${t.texto} vs. período anterior (${formatNumero(i?.loginsPeriodoAnterior)})` : undefined}
          cor={CORES.azul}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Cobertura de usuários"
          valor={formatPercentual(i?.cobertura)}
          detalhe={i ? `${i.usuariosAcessaram} de ${i.usuariosAtivos} usuários ativos acessaram` : undefined}
          cor={CORES.verde}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Dias com acesso"
          valor={formatNumero(i?.diasAtivos)}
          detalhe="dias distintos com pelo menos um login"
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Concentração no usuário que mais acessa"
          valor={formatPercentual(i?.concentracao)}
          detalhe={i?.dependeUmUsuario ? "depende de 1 usuário" : "dos logins do período"}
          cor={i?.dependeUmUsuario ? CORES.amarelo : undefined}
          isLoading={isLoading}
        />
      </GradeKpis>

      {Number.isFinite(id) && (
        <>
          <Engajamento periodo={periodo} idInstituicao={id} />
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-foreground">Usuários</h2>
            <TabelaUsuarios periodo={periodo} idInstituicao={id} />
          </section>
        </>
      )}
    </div>
  );
}
